// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/layout';
import Header from '../../../components/header';
import { AgGridReact } from 'ag-grid-react';
import Pagination from '../../../components/pagination';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import { PAGE_SIZE } from '../../../_common/constant';
import { useDashboardApi } from '../../../store/api/hooks/dashboardApiHook';
import ReportFilterBar from '../../../components/ReportFilterBar';
import Button from '../../../components/elements/button';
import { downloadExcel } from '../../../_common/functions';
import toast from 'react-hot-toast';

function hoursToHm(val: number): string {
  if (val == null || isNaN(val)) return '';
  const h = Math.floor(val);
  const m = Math.round((val - h) * 60);
  if (m === 60) return `${h + 1}h 0m`;
  return `${h}h ${m}m`;
}

function timeCellRenderer(params: any) {
  const val = params.value;
  if (val == null) return '';
  return <span>{hoursToHm(Number(val))}</span>;
}

function uptimeCellRenderer(params: any) {
  const val = params.value;
  if (val == null) return '';
  let color = '#dc2626';
  if (val >= 90) color = '#16a34a';
  else if (val >= 70) color = '#d97706';
  return <span style={{ color, fontWeight: 600 }}>{val}%</span>;
}

function downtimeCellRenderer(params: any) {
  const val = params.value;
  if (val == null) return '';
  return <span>{val}%</span>;
}

function statusCellRenderer(params: any) {
  const row = params.data;
  if (!row) return '';
  return (
    <span style={{
      background: row.colourCode ? `${row.colourCode}20` : '#f3f4f6',
      color: row.colourCode || '#6b7280',
      borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
    }}>{row.statusName}</span>
  );
}

const AssetsUptimeReport: React.FC = () => {
  const dashboardApi = useDashboardApi();
  const [rowData, setRowData] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [summary, setSummary] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const fetchData = useCallback((pageNum: number, filterParams: Record<string, any> = {}) => {
    setRowData(null);
    const params: any = { PageSize: PAGE_SIZE, PageNumber: pageNum };
    if (filterParams.facility) params.FacilityIDs = filterParams.facility;
    if (filterParams.subFacility) params.SubFacilityIDs = filterParams.subFacility;
    if (filterParams.assetGroup) params.AssetGroupIDs = filterParams.assetGroup;
    if (filterParams.department) params.DepartmentIDs = filterParams.department;
    if (filterParams.status) params.StatusIDs = filterParams.status;
    if (filterParams.startDate) params.StartDate = filterParams.startDate;
    if (filterParams.endDate) params.EndDate = filterParams.endDate;

    dashboardApi.getAssetsUptimeReport(
      params,
      (response: any) => {
        const d = response?.data || response;
        setRowData(d?.rows || []);
        setSummary(d?.summary);
        setTotalRecords(d?.summary?.totalAssets || 0);
        setFilterOptions([
          { key: 'facility', label: 'Facility', options: (d?.facilities || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'subFacility', label: 'Sub Facility', options: (d?.subFacilities || []).filter((f: any) => f.name && f.name.trim()).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'assetGroup', label: 'Asset Group', options: (d?.assetGroups || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'department', label: 'Department', options: (d?.departments || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'status', label: 'Status', options: (d?.statuses || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
        ]);
      },
      (error: any) => { console.error('Failed to fetch uptime report:', error); setRowData([]); }
    );
  }, []);

  useEffect(() => { fetchData(currentPage, filters); }, [currentPage]);

  const handleApplyFilter = (filterMap: Record<string, string>, startDate?: string, endDate?: string) => {
    const newFilters = { ...filterMap, startDate, endDate };
    setFilters(newFilters);
    setCurrentPage(1);
    fetchData(1, newFilters);
  };

  const handleExport = () => {
    const params: any = { PageSize: null, PageNumber: null };
    if (filters.facility) params.FacilityIDs = filters.facility;
    if (filters.subFacility) params.SubFacilityIDs = filters.subFacility;
    if (filters.assetGroup) params.AssetGroupIDs = filters.assetGroup;
    if (filters.department) params.DepartmentIDs = filters.department;
    if (filters.status) params.StatusIDs = filters.status;
    if (filters.startDate) params.StartDate = filters.startDate;
    if (filters.endDate) params.EndDate = filters.endDate;
    setIsExporting(true);
    dashboardApi.exportAssetsUptimeReportToXLS(
      params,
      (resp: any) => { downloadExcel(resp, 'assets_uptime_report'); toast.success('Exported successfully!'); setIsExporting(false); },
      (error: any) => { toast.error(error || 'Export failed'); setIsExporting(false); }
    );
  };

  const columnDefs = [
    { headerName: 'Asset Name', field: 'assetName', headerClass: 'bg-[#f0f0f0]', minWidth: 200, flex: 1, cellRenderer: titleCell, headerTooltip: 'Asset Name' },
    { headerName: 'Asset Code', field: 'assetCode', headerClass: 'bg-[#f0f0f0]', width: 160, cellRenderer: titleCell, headerTooltip: 'Asset Code' },
    { headerName: 'Facility', field: 'facilityName', headerClass: 'bg-[#f0f0f0]', width: 180, cellRenderer: titleCell, headerTooltip: 'Facility' },
    { headerName: 'Sub Facility', field: 'subFacilityName', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Sub Facility' },
    { headerName: 'Asset Group', field: 'assetGroupName', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Asset Group' },
    { headerName: 'Department', field: 'departmentName', headerClass: 'bg-[#f0f0f0]', width: 150, cellRenderer: titleCell, headerTooltip: 'Department' },
    { headerName: 'Status', field: 'statusName', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: statusCellRenderer, headerTooltip: 'Current Status' },
    { headerName: 'Uptime', field: 'totalUptime', headerClass: 'bg-[#f0f0f0]', width: 120, type: 'rightAligned', cellRenderer: timeCellRenderer, headerTooltip: 'Total Uptime (hrs)' },
    { headerName: 'Downtime', field: 'totalDowntime', headerClass: 'bg-[#f0f0f0]', width: 130, type: 'rightAligned', cellRenderer: timeCellRenderer, headerTooltip: 'Total Downtime (hrs)' },
    { headerName: 'Uptime %', field: 'uptimePct', headerClass: 'bg-[#f0f0f0]', width: 110, type: 'rightAligned', cellRenderer: uptimeCellRenderer, headerTooltip: 'Uptime Percentage' },
    { headerName: 'Downtime %', field: 'downtimePct', headerClass: 'bg-[#f0f0f0]', width: 120, type: 'rightAligned', cellRenderer: downtimeCellRenderer, headerTooltip: 'Downtime Percentage' },
  ];

  return (
    <Layout>
      <Header title="Assets Uptime Report" back="/reports">
        <Header.Right>
          <Button theme='primary' onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </Header.Right>
      </Header>
      <div className="p-4 pb-0">
        <ReportFilterBar availableFilters={filterOptions} onApply={handleApplyFilter} />
        {summary && (
          <div className="bg-white rounded-lg mb-4 flex items-center gap-6 flex-wrap text-[13px]" style={{ padding: '12px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <span style={{ color: '#111827', fontWeight: 600 }}>Total Assets: {summary.totalAssets}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#16a34a', fontWeight: 600 }}>Avg Uptime: {summary.avgUptimePct?.toFixed(1)}%</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#dc2626', fontWeight: 600 }}>With Downtime: {summary.assetsWithDowntime}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#111827', fontWeight: 600 }}>Total Downtime: {hoursToHm(Number(summary.totalDowntimeHours || 0))}</span>
          </div>
        )}
      </div>
      <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
        <Pagination perPage={PAGE_SIZE} totalRecords={totalRecords} onPageChange={setCurrentPage} currentPage={currentPage} />
      </div>
      <div className='bg-[#FAFAFA] w-full'>
        <div className="ag-theme-alpine px-4 w-full h-1/2">
          <AgGridReact
            columnDefs={columnDefs as any}
            rowData={rowData || []}
            rowSelection="multiple"
            pagination={false}
            domLayout="autoHeight"
            loadingOverlayComponent={Loader}
            noRowsOverlayComponent={() => (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">No Data Found</div>
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </Layout>
  );
};

export default AssetsUptimeReport;
